/*
 * Lemonade Training Oy copy rights all right reserved.
 * Schedule Board Type Definitions (F026)
 *
 * The dashboard schedule's view and density choices, and the per-box counter
 * that records when staff change them. The counter lives in
 * Boxes/{boxID}/ScheduleTemplateEvents next to the template events, is written
 * only by the recordScheduleViewChanged callable (client writes are denied by
 * rules) and never carries a user id.
 */

import type { Timestamp } from "firebase/firestore";

export const SCHEDULE_VIEWS = [
  "lanes",
  "agenda",
  "rooms",
  "coach",
  "classic",
] as const;
export type ScheduleView = (typeof SCHEDULE_VIEWS)[number];

export const SCHEDULE_DENSITIES = ["compact", "comfortable"] as const;
export type ScheduleDensity = (typeof SCHEDULE_DENSITIES)[number];

export const DEFAULT_SCHEDULE_VIEW: ScheduleView = "lanes";
export const DEFAULT_SCHEDULE_DENSITY: ScheduleDensity = "comfortable";

export function isScheduleView(value: unknown): value is ScheduleView {
  return (SCHEDULE_VIEWS as readonly unknown[]).includes(value);
}

export function isScheduleDensity(value: unknown): value is ScheduleDensity {
  return (SCHEDULE_DENSITIES as readonly unknown[]).includes(value);
}

/** The server rejects anything outside this shape with invalid-argument. */
export type RecordScheduleViewChangedRequest = {
  boxID: string;
  view: ScheduleView;
  density?: ScheduleDensity;
};

export type RecordScheduleViewChangedResponse = {
  /** Box-local day the call was counted in, "YYYY-MM-DD". */
  day: string;
};

export const SCHEDULE_VIEW_CHANGED_TYPE = "schedule_view_changed" as const;

/** Enforced by a Firestore TTL policy on ScheduleTemplateEvents.expiresAt. */
export const SCHEDULE_VIEW_COUNTER_RETENTION_DAYS = 90;

/** One document per box, view and box-local day; merged, never overwritten. */
export type ScheduleViewChangedCounterDoc = {
  type: typeof SCHEDULE_VIEW_CHANGED_TYPE;
  boxID: string;
  view: ScheduleView;
  /** Box-local day "YYYY-MM-DD", computed server-side in the box timezone. */
  day: string;
  count: number;
  /** Density sent on the latest call that sent one; absent if none did. */
  lastDensity?: ScheduleDensity;
  /** scheduleViewCounterExpiresAtMillis(day): a merge never moves it. */
  expiresAt: Timestamp | Date;
};

/** 00:00 UTC of a "YYYY-MM-DD" day plus the retention. */
export function scheduleViewCounterExpiresAtMillis(day: string): number {
  const [year, month, date] = day.split("-").map(Number);
  return (
    Date.UTC(year, month - 1, date) +
    SCHEDULE_VIEW_COUNTER_RETENTION_DAYS * 86400000
  );
}

export function scheduleViewCounterDocID(
  boxID: string,
  view: ScheduleView,
  day: string,
): string {
  return `view_${boxID}_${view}_${day}`;
}

/**
 * The per-user budget `updateClass` enforces while `rateLimitUpdateClass` is on:
 * a ceiling on runaway fan-out, not a tight budget (the dashboard's bulk edit of a
 * full 300-class week runs 5 calls at a time and peaks below 20 per second). A
 * refused client waits one `windowMs` before retrying, so the budget has refilled.
 */
export const UPDATE_CLASS_RATE_LIMIT = {
  key: "updateClass",
  limit: 200,
  windowMs: 10 * 1000,
} as const;
